import { Box, Grid, Typography } from '@mui/material';
import useMyDropdowns from 'hooks/useMyDropdowns';
import { HierarchySection } from 'ui-component/customFormsComponents';
import { IRecordFieldWithFieldName, IRecordHeaders, IRoleAccess } from 'ui-component/records/types';
import { getEditableField, NON_EDITABLE_FIELDS } from 'ui-component/records/utils';
import { ListValue } from 'views/backoffice/CustomLists/types';
import { ObjectProperty } from 'views/backoffice/CustomObjects/types';
import {
    FIELD_CONDITIONAL_THEN_OPERATOR_LIST,
    FieldConditional,
    FieldConditionalThenOperatorEnum,
    FormSectionWithChildren
} from 'views/DataFocusView/types';
import { filterFieldsEnabledByRecordType, orderArrayByAttribute } from 'views/DataFocusView/utils';
import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import ShipmentsSection from 'ui-component/customFormsComponents/ShipmentsSection';
import SectionField from '../SectionField';

export type SectionRenderProps = {
    recordId?: string;
    recordData?: IRecordHeaders;
    section: FormSectionWithChildren;
    headers: Record<string, IRecordFieldWithFieldName>;
    recordTypeId: string;
    listValuesByListId: Record<number, ListValue[]>;
    propertiesById: Record<number, ObjectProperty>;
    propertyByFieldName: Record<string, Record<string, ObjectProperty>>;
    renderConditions?: FieldConditional[];
    fullfilledConditionsIds?: string[];
    fieldAccessList: IRoleAccess[];
    hideNonEditableFields?: boolean;
    activeDataFocusView?: boolean;
    loadingListValues?: boolean;
    forcedWidth?: number;
    setRequiredFields?: React.Dispatch<React.SetStateAction<Array<{ sectionName: string; field: string }>>>;
    inputRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
};

export type SectionRenderHandle = {
    scrollToField: (fieldName: string) => void;
    scrollSectionIntoView: (options?: ScrollIntoViewOptions) => void;
};

const SectionRender = forwardRef<SectionRenderHandle, SectionRenderProps>(
    (
        {
            recordId,
            recordData,
            section,
            headers,
            listValuesByListId,
            recordTypeId,
            propertiesById,
            propertyByFieldName,
            renderConditions,
            fullfilledConditionsIds,
            fieldAccessList,
            hideNonEditableFields,
            activeDataFocusView,
            loadingListValues,
            forcedWidth,
            setRequiredFields,
            inputRefs
        }: SectionRenderProps,
        ref
    ) => {
        const gridRef = useRef<HTMLDivElement>(null);
        // const itemRef = useRef<HTMLDivElement>(null);
        const sectionBoxRef = useRef<HTMLDivElement>(null);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        // const [containerWidth, setContainerWidth] = useState(forcedWidth && gridRef.current ? gridRef.current.clientWidth : 0);

        // const [itemHeight, setItemHeight] = useState(42);

        // useEffect(() => {
        //     if (!gridRef.current) return undefined;
        //     const observer = new window.ResizeObserver(([entry]) => {
        //         setContainerWidth(entry.contentRect.width);
        //     });
        //     observer.observe(gridRef.current);
        //     return () => {
        //         observer.disconnect();
        //     };
        // }, []);

        // useEffect(() => {
        //     if (!itemRef.current) return;
        //     const observer = new window.ResizeObserver(([entry]) => {
        //         setItemHeight(entry.contentRect.height);
        //     });
        //     observer.observe(itemRef.current);
        //     return () => observer.disconnect();
        // }, []);

        const orderedSectionFields = orderArrayByAttribute(section.sectionFields);

        const { problemCodeList, recordStatusList, usersList, targetTenantList } = useMyDropdowns({
            recordTypeId: +recordTypeId
        });

        const getFieldThenOrOtherwiseConditions = React.useCallback(
            (fieldName: string, objectPropertyName?: string) =>
                renderConditions?.filter(({ then, otherwise }) => {
                    const isProperty = !!objectPropertyName;

                    if (
                        isProperty &&
                        (then.some((el) => el.field === fieldName && objectPropertyName === el.objectProperty) ||
                            (otherwise.field === fieldName && objectPropertyName === otherwise.objectProperty))
                    ) {
                        return true;
                    }

                    if (!isProperty && (then.some((el) => el.field === fieldName) || otherwise.field === fieldName)) {
                        return true;
                    }

                    return false;
                }),
            [renderConditions]
        );

        const enabledFieldFilter = React.useCallback(
            () => filterFieldsEnabledByRecordType(headers, propertyByFieldName),
            [headers, propertyByFieldName]
        );

        const filteredSectionFields = React.useMemo(
            () =>
                orderedSectionFields
                    .filter(({ baseField, recordAdditionalFieldsByType }) => {
                        const fieldDef = headers[baseField || recordAdditionalFieldsByType?.name];
                        if (!fieldDef) return true;
                        const isNotEditable = NON_EDITABLE_FIELDS.includes(fieldDef?.fieldName as (typeof NON_EDITABLE_FIELDS)[number]);
                        return hideNonEditableFields ? !isNotEditable : true;
                    })
                    .filter(enabledFieldFilter)
                    .filter(({ baseField, recordAdditionalFieldsByType, objectProperty, name }) => {
                        const isField = !!baseField || !!recordAdditionalFieldsByType;
                        const fieldDef = headers[baseField || recordAdditionalFieldsByType?.name];
                        const propertyDef = objectProperty ? propertiesById[+objectProperty.id] : undefined;

                        if (!fieldDef && !propertyDef) {
                            return false;
                        }

                        if (!fieldDef?.gridEditable) {
                            return false;
                        }

                        const dataType = fieldDef?.dataType ?? propertyDef?.dataType;
                        if (dataType && dataType.toLowerCase() === 'array') {
                            return false;
                        }

                        const usedConditions = getFieldThenOrOtherwiseConditions(
                            isField ? fieldDef?.fieldName ?? '' : propertyDef?.objectDefinition?.name ?? '',
                            propertyDef?.name
                        );

                        const usedAndFullfiledConditions = usedConditions?.filter((el) => fullfilledConditionsIds?.includes(el.id)) || [];
                        const usedAndNonFullfiledConditions =
                            usedConditions?.filter((el) => !fullfilledConditionsIds?.includes(el.id)) || [];

                        const actionsByConditions: (typeof FIELD_CONDITIONAL_THEN_OPERATOR_LIST)[FieldConditionalThenOperatorEnum][] = [];

                        for (const { then } of usedAndFullfiledConditions) {
                            const thenAppliedToThisField = then.filter((el) => {
                                const isProperty = !!propertyDef?.name;
                                return !isProperty ? el.field === fieldDef?.fieldName || '' : el.objectProperty === propertyDef?.name;
                            });
                            actionsByConditions.push(
                                ...thenAppliedToThisField.map((el) => FIELD_CONDITIONAL_THEN_OPERATOR_LIST[el.operator])
                            );
                        }

                        for (const { otherwise } of usedAndNonFullfiledConditions) {
                            const otherwiseAppliedToThisField =
                                otherwise.field === (fieldDef?.fieldName || '') || otherwise.objectProperty === (propertyDef?.name || '');
                            if (otherwiseAppliedToThisField)
                                actionsByConditions.push(FIELD_CONDITIONAL_THEN_OPERATOR_LIST[otherwise.operator]);
                        }

                        return !actionsByConditions.includes(FIELD_CONDITIONAL_THEN_OPERATOR_LIST.HIDDEN);
                    })
                    .filter(Boolean),
            [
                orderedSectionFields,
                headers,
                hideNonEditableFields,
                propertiesById,
                fullfilledConditionsIds,
                getFieldThenOrOtherwiseConditions,
                enabledFieldFilter
            ]
        );

        React.useEffect(() => {
            if (!setRequiredFields) return;
            const required = section.sectionFields
                .map((field) => {
                    const fieldDef = headers[field.baseField || field.recordAdditionalFieldsByType?.name];
                    if (
                        (fieldDef?.required || field.isRequired) &&
                        fieldDef?.gridEditable &&
                        getEditableField(fieldDef?.fieldName || '', fieldAccessList)
                    ) {
                        return {
                            sectionName: section.name,
                            field: fieldDef?.fieldName || field.recordAdditionalFieldsByType?.name
                        };
                    }
                    return null;
                })
                .filter(Boolean);

            setRequiredFields((prev?: any[]) => {
                const merged = new Set([...(prev ?? []), ...(required as any[])]);
                return Array.from(merged);
            });
        }, [section.sectionFields, headers, fieldAccessList, setRequiredFields, section.id, section.name]);

        useImperativeHandle(
            ref,
            () => ({
                scrollToField: (fieldName: string) => {
                    const elementId = `${section.id}-${fieldName}`;
                    const element = document.getElementById(elementId);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                },
                scrollSectionIntoView: (options?: ScrollIntoViewOptions) => {
                    if (sectionBoxRef.current) {
                        sectionBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', ...options });
                    }
                }
            }),
            [section.id]
        );

        if (section.specialComponent?.includes('HIERARCHY') && recordData && recordId)
            return (
                <Box id="hierarchy-section" sx={{ maxWidth: forcedWidth ? `calc(96.5vw - ${forcedWidth}px)` : '100%' }}>
                    <HierarchySection recordId={recordId} initialRecord={recordData} />
                </Box>
            );

        if (section.specialComponent?.includes('SHIPMENTS'))
            return (
                <Box id="shipments-section">
                    <ShipmentsSection
                        section={section}
                        filteredSectionFields={filteredSectionFields}
                        forcedWidth={forcedWidth}
                        headers={headers}
                        listValuesByListId={listValuesByListId}
                        propertiesById={propertiesById}
                        fieldAccessList={fieldAccessList}
                        fullfilledConditionsIds={fullfilledConditionsIds}
                        activeDataFocusView={activeDataFocusView}
                        loadingListValues={loadingListValues}
                        inputRefs={inputRefs}
                        getFieldThenOrOtherwiseConditions={getFieldThenOrOtherwiseConditions}
                    />
                </Box>
            );

        if (filteredSectionFields.length === 0) return null;

        return (
            <Box ref={sectionBoxRef} sx={{ mb: '1rem', px: '1.5rem', py: '0.5rem', borderRadius: '0.5rem', border: 'solid 1px #EBEEFE' }}>
                <Typography fontSize="1.25rem" fontWeight={500} sx={{ lineHeight: '160%', mb: '1rem' }} color="secondary">
                    {section.name}
                </Typography>
                <Grid container ref={gridRef} spacing={1} sx={{ maxWidth: forcedWidth ? `calc(95vw - ${forcedWidth}px)` : '100%' }}>
                    {filteredSectionFields.map((filteredField) => (
                        <SectionField
                            key={`${section.id}-${filteredField.name}`}
                            filteredField={filteredField}
                            section={section}
                            headers={headers}
                            propertiesById={propertiesById}
                            listValuesByListId={listValuesByListId}
                            recordStatusList={recordStatusList}
                            problemCodeList={problemCodeList}
                            usersList={usersList}
                            targetTenantList={targetTenantList}
                            fieldAccessList={fieldAccessList}
                            fullfilledConditionsIds={fullfilledConditionsIds}
                            activeDataFocusView={activeDataFocusView}
                            loadingListValues={loadingListValues}
                            inputRefs={inputRefs}
                            getFieldThenOrOtherwiseConditions={getFieldThenOrOtherwiseConditions}
                            recordId={recordId}
                            recordTypeId={recordTypeId}
                        />
                    ))}

                    {!!section.children?.length && (
                        <Grid item container xs={12} spacing={1} sx={{ maxWidth: forcedWidth ? `calc(95vw - ${forcedWidth}px)` : '100%' }}>
                            {section.children.map((childSection) => (
                                <Grid item xs={section.columns} key={childSection.id}>
                                    <SectionRender
                                        ref={undefined}
                                        section={childSection}
                                        renderConditions={renderConditions}
                                        fullfilledConditionsIds={fullfilledConditionsIds}
                                        headers={headers}
                                        recordTypeId={recordTypeId}
                                        propertiesById={propertiesById}
                                        propertyByFieldName={propertyByFieldName}
                                        fieldAccessList={fieldAccessList}
                                        listValuesByListId={listValuesByListId}
                                        hideNonEditableFields={hideNonEditableFields}
                                        activeDataFocusView={activeDataFocusView}
                                        loadingListValues={loadingListValues}
                                        forcedWidth={forcedWidth}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    }
);

export default SectionRender;
